import React, { useState, useMemo } from 'react';
import Icon from '../../../components/AppIcon';
import TeamMemberCard from './TeamMemberCard';
import Button from '../../../components/ui/Button';

const TeamSection = ({ 
  title, 
  members, 
  icon, 
  description, 
  isExpanded, 
  onToggleExpand,
  activeExpertise,
  activeExperience 
}) => {
  const [showAll, setShowAll] = useState(false);

  const filteredMembers = useMemo(() => {
    let filtered = [...members];

    // Filter by expertise
    if (activeExpertise) {
      filtered = filtered.filter(member => 
        member.expertise.includes(activeExpertise)
      );
    }

    // Filter by experience
    if (activeExperience && activeExperience !== 'all') {
      filtered = filtered.filter(member => {
        const exp = member.experience;
        switch (activeExperience) {
          case 'junior':
            return exp >= 2 && exp < 5;
          case 'mid':
            return exp >= 5 && exp < 10;
          case 'senior':
            return exp >= 10;
          default:
            return true;
        }
      });
    }

    return filtered;
  }, [members, activeExpertise, activeExperience]);

  const displayedMembers = showAll ? filteredMembers : filteredMembers.slice(0, 6);
  const hasMore = filteredMembers.length > 6;

  if (filteredMembers.length === 0) {
    return null;
  }

  return (
    <div className="mb-12">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 rounded-full gradient-primary flex items-center justify-center">
            <Icon name={icon} size={20} color="white" />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-foreground">{title}</h2>
            <p className="text-muted-foreground">{description}</p>
          </div>
        </div>
        
        <div className="flex items-center space-x-2">
          <span className="text-sm text-muted-foreground">
            {filteredMembers.length} member{filteredMembers.length !== 1 ? 's' : ''}
          </span>
          <Button
            variant="ghost"
            size="sm"
            iconName={isExpanded ? "ChevronUp" : "ChevronDown"}
            onClick={onToggleExpand}
          >
            {isExpanded ? 'Collapse' : 'Expand'}
          </Button>
        </div>
      </div>

      {isExpanded && (
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {displayedMembers.map((member) => (
              <TeamMemberCard
                key={member.id}
                member={member}
                variant="default"
              />
            ))}
          </div>

          {hasMore && (
            <div className="text-center">
              <Button
                variant="outline"
                iconName={showAll ? "ChevronUp" : "ChevronDown"}
                iconPosition="right"
                onClick={() => setShowAll(!showAll)}
              >
                {showAll ? 'Show Less' : `Show ${filteredMembers.length - 6} More`}
              </Button>
            </div>
          )}

          {filteredMembers.length === 0 && (
            <div className="text-center py-12">
              <Icon name="Users" size={48} className="text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-foreground mb-2">No team members found</h3>
              <p className="text-muted-foreground">
                Try adjusting your filters to see more team members.
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default TeamSection;